import { useAtom, useSetAtom } from "jotai";
import Axios, { AxiosResponse } from 'axios';
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    brandsAtom,
    brandsLoadingAtom,
    IBrand,
    IChannelPayload,
    IUploadLogoRequest,
    loggedInBrandAtom,
    singleBrandAtom,
    singleBrandLoadingAtom
} from "./types";

type LoggedInBrand = IBrand | null;
type SetBrandType = (payload: IBrand[]) => void;
type FetchBrandsType = () => void;
type GetBrandById = (id: number) => IBrand;
type GetBrand = (id: number) => Promise<IBrand | null>;
type UpdateBrand = (payload: Partial<IBrand>) => Promise<IBrand | null>;
type UploadLogoType = (payload: IUploadLogoRequest) => Promise<void>;
type UploadStaticHeaderType = (payload: FormData) => Promise<{ header_image: string } | null>;
type connectChannelType = (payload: IChannelPayload) => Promise<void>;
type ManualConnectChannelType = (payload: { client_id: string; client_secret: string; store_domain: string }) => Promise<any>;
type VerifyShopifyConnectionType = () => Promise<{ connected: boolean }>;
type SetDesignerAvailabilityType = (value: boolean) => Promise<IBrand | null>;
type SetPricingProtectionType = (brandId: number, value: boolean) => Promise<IBrand | null>;
type UpdateStorefrontDomainType = (domain: string) => Promise<IBrand | null>;
type UpdateBrandNameType = (name: string) => Promise<IBrand | null>;

type BrandKeyFunctions = {
    get: FetchBrandsType;
    getById: GetBrandById,
    getBrand: GetBrand,
    update: UpdateBrand,
    uploadLogo: UploadLogoType,
    uploadStaticHeader: UploadStaticHeaderType,
    connectChannel: connectChannelType,
    manualConnectChannel: ManualConnectChannelType,
    verifyShopifyConnection: VerifyShopifyConnectionType,
    setDesignerAvailability: SetDesignerAvailabilityType,
    setPricingProtection: SetPricingProtectionType,
    updateStorefrontDomain: UpdateStorefrontDomainType,
    updateBrandName: UpdateBrandNameType
}

type UseBrands = [
    IBrand[],
    LoggedInBrand,
    IBrand | null,
    SetBrandType,
    BrandKeyFunctions
];

// #region useBrands
export const useBrands = (): UseBrands => {
    const [brands, setBrands] = useAtom(brandsAtom);
    const [loggedInBrand, setLoggedInBrand] = useAtom(loggedInBrandAtom)
    const setBrandsLoading = useSetAtom(brandsLoadingAtom);

    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [user, authToken, loginFunctions] = useLogin();

    const [singleBrand, setSingleBrand] = useAtom(singleBrandAtom);
    const setSingleBrandLoading = useSetAtom(singleBrandLoadingAtom);

    /**
     * Loads the CALLER'S OWN brand — not the brand table.
     *
     * This used to fetch every brand into `brandsAtom` so dropdowns could filter
     * it client-side. That silently lost every brand outside the fetched window
     * (and every inactive/unnamed brand), which is why brand pickers could not
     * find real brands. Pickers now search the database directly
     * (`useBrandSearch`) and labels resolve through `useBrandNames`, so the only
     * thing still worth holding in memory is the logged-in brand itself.
     *
     * `brandsAtom` is kept populated with just that one brand so existing
     * consumers keep working; it is NOT a directory of brands.
     */
    const getBrands = () => {
        const fetchOwnBrand = async (): Promise<void> => {
            const brandId = user?.brand_id;
            if (!brandId) {
                setLoggedInBrand(null);
                return;
            }

            setBrandsLoading(true);
            const results = await Axios.get(`${environment.brandsUrl}/${brandId}`, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (results) {
                const own = results.data as IBrand;
                setLoggedInBrand(own);
                setBrands([own]);
            } else {
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: 'There was an error while getting your brand. Please try again later.'
                });
            }
            setBrandsLoading(false);
        }

        fetchOwnBrand();
    }

    /**
     * @deprecated Only ever resolves the logged-in brand now that the full brand
     * list is no longer fetched. For any other brand use `getBrand(id)` (one
     * request) or `useBrandNames` (cached id -> name).
     */
    const getById: GetBrandById = (id: number): IBrand => {
        return brands.find((value: IBrand) => value.id === id) as IBrand;
    }

    const getBrand = async (id: number): Promise<IBrand | null> => {
        setSingleBrandLoading(true);
        if (!authToken) return null;
        const getResponse: AxiosResponse<IBrand> | void = await Axios.get(`${environment.brandsUrl}/${id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getResponse) {
            setSingleBrandLoading(false);
            setSingleBrand(getResponse.data);
            return getResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting brand by id.'
            });
            setSingleBrandLoading(false);
            return null;
        }
    }

    const update = async (payload: Partial<IBrand>): Promise<IBrand | null> => {
        const updateResponse: AxiosResponse<IBrand> | void = await Axios.put(environment.brandsUrl, payload, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (updateResponse) {
            const saved: IBrand = updateResponse.data;
            // The PUT echoes back only the persisted columns, so merge rather than
            // replace — decorated fields (channels, logos, header) live only on the
            // cached copies. Every atom holding this brand is refreshed here so no
            // screen keeps rendering pre-update values until a reload.
            setLoggedInBrand((prev: IBrand | null) => {
                if (!prev) return saved;
                // A superadmin editing someone else's brand must not overwrite their own.
                return prev.id === saved.id ? { ...prev, ...saved } : prev;
            });
            setSingleBrand((prev: IBrand | null) => prev && prev.id === saved.id ? { ...prev, ...saved } : prev);
            setBrands((prev: IBrand[]) => prev.map((brand: IBrand) => brand.id === saved.id
                ? { ...brand, ...saved }
                : brand));
            return saved;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while updating brand.'
            });
            return null;
        }
    }

    const uploadLogo = async (payload: IUploadLogoRequest): Promise<void> => {
        setSingleBrandLoading(true);
        const uploadResponse: AxiosResponse<string> = await Axios.post(`${environment.brandsUrl}/logo`, payload, {
            headers: {
                "Content-Type": "multipart/form-data",
                'X-Wallmates-Auth': authToken
            }
        });
        if (uploadResponse) {
            const updatedBrand: IBrand = {
                id: payload.id,
                name: payload.name,
                shopify_brand_name: payload.shopify_brand_name
            }
            if (payload.logo) {
                updatedBrand.brand_logo = uploadResponse.data;
            }
            if (payload.rect_logo) {
                updatedBrand.rect_logo = uploadResponse.data;
            }
            setSingleBrand(updatedBrand);
            setSingleBrandLoading(false);
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while uploading brand logo.'
            });
            setSingleBrandLoading(false);
        }
    }

    const uploadStaticHeader = async (payload: FormData): Promise<{ header_image: string } | null> => {
        const uploadResponse: AxiosResponse<{ header_image: string }> | void = await Axios.put(`${environment.brandsUrl}/header`, payload, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (uploadResponse) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Header uploaded successfully.'
            });
            return uploadResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while uploading header.'
            });
            return null;
        }
    }

    const connectChannel = async (payload: IChannelPayload): Promise<void> => {
        const updateResponse = await Axios.post(`${environment.brandChannelUrl}/connect-channel`, payload, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (updateResponse) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Channel settings updated successfully.'
            });
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while updating channel settings.'
            });
        }
    }

    const manualConnectChannel: ManualConnectChannelType = async (payload) => {
        const response = await Axios.post(`${environment.brandChannelUrl}/manual-connect`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            return response.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while connecting channel.'
            });
            return null;
        }
    }

    const verifyShopifyConnection: VerifyShopifyConnectionType = async () => {
        const response = await Axios.get(`${environment.brandChannelUrl}/verify-shopify-connection`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            return response.data;
        }
        return { connected: false };
    }

    const setDesignerAvailability: SetDesignerAvailabilityType = async (value: boolean): Promise<IBrand | null> => {
        const response: AxiosResponse<IBrand> | void = await Axios.put(`${environment.brandsUrl}/designer-availability`, {
            available_to_designers: value
        }, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: `Interior designer catalog ${value ? 'enabled' : 'disabled'} successfully.`
            });
            return response.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while updating designer availability.'
            });
            return null;
        }
    }

    /**
     * Toggles the brand's permanent exemption from the admin account-type pricing
     * sync. SUPERADMIN-only server side; a 403 resolves to null so the caller can
     * revert its optimistic state.
     *
     * Takes an explicit brandId because an admin is acting on ANOTHER brand — this
     * never reads the logged-in brand from the token.
     */
    const setPricingProtection: SetPricingProtectionType = async (brandId: number, value: boolean): Promise<IBrand | null> => {
        const response: AxiosResponse<IBrand> | void = await Axios.put(`${environment.brandsUrl}/${brandId}/pricing-protection`, {
            pricing_protected: value
        }, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            setSnackbar({
                show: true,
                snackbarLevel: value ? 'info' : 'warn',
                text: value
                    ? 'Pricing protection enabled for this brand. Account type pricing syncs will always skip it.'
                    : 'Pricing protection disabled. This brand is now exposed to future account type pricing syncs.'
            });
            return response.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while updating pricing protection.'
            });
            return null;
        }
    }

    /**
     * Persists only the storefront domain via the shared `PUT /brands` endpoint.
     * Merges the result into the brand atoms rather than replacing them so the
     * decorated fields (channels, logos, header) survive, letting the backfill
     * gate re-evaluate without a page reload.
     */
    const updateStorefrontDomain: UpdateStorefrontDomainType = async (domain: string): Promise<IBrand | null> => {
        const brandId = loggedInBrand?.id ?? user?.brand_id;

        if (!brandId) {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'Could not determine your brand. Please refresh and try again.'
            });
            return null;
        }

        try {
            const response: AxiosResponse<IBrand> = await Axios.put(
                environment.brandsUrl,
                { id: brandId, storefront_domain: domain },
                { headers: { 'X-Wallmates-Auth': authToken } }
            );

            const saved = response.data?.storefront_domain ?? domain;

            setLoggedInBrand((prev: IBrand | null) => prev ? { ...prev, storefront_domain: saved } : prev);
            setSingleBrand((prev: IBrand | null) => prev?.id === brandId ? { ...prev, storefront_domain: saved } : prev);
            setBrands((prev: IBrand[]) => prev.map((brand: IBrand) => brand.id === brandId
                ? { ...brand, storefront_domain: saved }
                : brand));

            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Store website saved.'
            });

            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'There was an error while saving your store website.'
            });
            return null;
        }
    }

    /**
     * Renames the logged in brand via the dedicated `PUT /brands/name` endpoint,
     * which derives the brand id from the auth token. Merges into the atoms
     * rather than replacing them so decorated fields survive and the new name
     * shows everywhere without a reload.
     */
    const updateBrandName: UpdateBrandNameType = async (name: string): Promise<IBrand | null> => {
        const brandId = loggedInBrand?.id ?? user?.brand_id;

        try {
            const response: AxiosResponse<IBrand> = await Axios.put(
                `${environment.brandsUrl}/name`,
                { name },
                { headers: { 'X-Wallmates-Auth': authToken } }
            );

            const saved = response.data?.name ?? name;

            setLoggedInBrand((prev: IBrand | null) => prev ? { ...prev, name: saved } : prev);
            setSingleBrand((prev: IBrand | null) => prev && prev.id === brandId ? { ...prev, name: saved } : prev);
            setBrands((prev: IBrand[]) => prev.map((brand: IBrand) => brand.id === brandId
                ? { ...brand, name: saved }
                : brand));

            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Brand name saved.'
            });

            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'There was an error while saving your brand name.'
            });
            return null;
        }
    }

    const keyFunctions: BrandKeyFunctions = {
        get: getBrands,
        getById,
        getBrand,
        update,
        uploadLogo,
        uploadStaticHeader,
        connectChannel,
        manualConnectChannel,
        verifyShopifyConnection,
        setDesignerAvailability,
        setPricingProtection,
        updateStorefrontDomain,
        updateBrandName
    }

    return [
        brands,
        loggedInBrand,
        singleBrand,
        setBrands,
        keyFunctions
    ]
}